import { Paper, Typography } from '@material-ui/core/';
import type { Theme } from '@material-ui/core/styles';
import { createStyles, makeStyles, withStyles } from '@material-ui/core/styles';
import type { WithStylesOptions } from '@material-ui/core/styles/withStyles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';

import { ReactComponent as BSCLogo } from '../../../common/svg/button-icons/bsc-logo.svg';
import { ReactComponent as EthLogo } from '../../../common/svg/button-icons/eth-icon.svg';
import { useSwitchChain, useWeb3State } from '../../../hooks';
import { WEB3_CONFIG } from '../../../lib';
import styles from './MarketplaceToggler.module.css';

const useStyles = makeStyles<Theme, { sidebar?: boolean; open?: boolean }>(
  theme =>
    createStyles({
      paper: {
        display: 'flex',
        flexWrap: 'wrap',
        borderRadius: '25px',
        border: ({ sidebar }) =>
          sidebar ? `1px solid black` : `1px solid white`,
        backgroundColor: ({ sidebar }) => (sidebar ? 'black' : '#fcd559'),
        width: ({ open, sidebar }) => (sidebar && open ? '194px' : 'auto'),
        height: ({ open, sidebar }) => (sidebar && open ? '31px' : 'auto'),
      },
      divider: {
        margin: theme.spacing(1, 0.5),
      },
      buttonColor: {
        backgroundColor: '#e6b000 !important',
        border: '1px solid rgba(0,0,0,0) !important',
      },
      buttonHover: {
        border: '1px solid rgba(0,0,0,0) !important',
        width: ({ open, sidebar }) => (sidebar && open ? '97px' : 'auto'),
        height: ({ open, sidebar }) => (sidebar && open ? '27px' : 'auto'),
        '&:hover': {
          border: '1px solid var(--new-btn-yellow) !important',
        },
      },
    }),
);

const StyledToggleButtonGroup = withStyles<
  'grouped',
  WithStylesOptions<Theme>,
  { open?: boolean }
>(theme => ({
  grouped: {
    margin: theme.spacing(0.3),
    border: 'none',
    '&:not(:first-child)': {
      borderRadius: ({ open }) => (open ? '0 25px 25px 0' : '0 0 25px 25px'),
    },
    '&:first-child': {
      borderRadius: ({ open }) => (open ? '25px 0 0 25px' : '25px 25px 0 0'),
    },
  },
}))(ToggleButtonGroup);

export interface MarketplaceToggleProps {
  open: boolean;
  errorMsg?: string;
  reload?: boolean;
  sidebar?: boolean;
  className?: string;
}

export const MarketplaceToggle = ({
  open,
  className,
  errorMsg = 'An error occurred, please try again.',
  reload = true,
  sidebar,
}: MarketplaceToggleProps) => {
  const { walletDetails } = useWeb3State();
  const { switchNetworkAsync } = useSwitchChain();

  const handleToggle = useCallback(
    async (_: unknown, newChainId: string | null) => {
      try {
        const targetChainId = Number(newChainId);

        await switchNetworkAsync(targetChainId);

        if (!reload) {
          return;
        }

        window.location.reload();
      } catch (e) {
        console.error(e);
        toast.error(errorMsg);
      }
    },
    [switchNetworkAsync],
  );

  const classes = useStyles({ sidebar, open });

  return (
    <Paper elevation={0} className={clsx(classes.paper, className)}>
      <StyledToggleButtonGroup
        open={open}
        size="small"
        value={walletDetails.chain.id}
        exclusive
        onChange={handleToggle}
        aria-label="marketplace"
        orientation={open ? 'horizontal' : 'vertical'}
      >
        <ToggleButton
          value={WEB3_CONFIG.chains.eth.id}
          aria-label={WEB3_CONFIG.chains.eth.name}
          classes={{ selected: classes.buttonColor }}
          className={classes.buttonHover}
        >
          <EthLogo className="w-4 h-4" />
          <Typography className={styles.buttonText}>ETH</Typography>
        </ToggleButton>
        <ToggleButton
          value={WEB3_CONFIG.chains.bsc.id}
          aria-label={WEB3_CONFIG.chains.bsc.name}
          classes={{ selected: classes.buttonColor }}
          className={classes.buttonHover}
        >
          <BSCLogo className="w-4 h-4 pr-1" />
          <Typography className={styles.buttonText}>BSC</Typography>
        </ToggleButton>
      </StyledToggleButtonGroup>
    </Paper>
  );
};
